// Naplánované úlohy: kdy se spouští a jak to říct česky. Čas je vždy pražský (letní/zimní čas řeší Intl).

export type ScheduleFreq = 'daily' | 'weekdays' | 'weekly' | 'once';

export interface ScheduleWhen {
  freq: ScheduleFreq;
  /** 1 = pondělí … 7 = neděle (jen u weekly). */
  weekday: number | null;
  /** `HH:MM` (u once čas z runAt, jen pro zobrazení) */
  time: string;
  /** Jen u once: kdy proběhne (ISO okamžik). */
  runAt?: string | null;
}

export interface ScheduleDTO extends ScheduleWhen {
  id: string;
  title: string;
  prompt: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: 'ok' | 'error' | null;
  lastError: string | null;
  lastConversationId: string | null;
  createdAt: string;
}

export const SCHEDULE_TZ = 'Europe/Prague';
export const SCHEDULE_LIMIT = 20;
export const WEEKDAY_NAMES = ['pondělí', 'úterý', 'středa', 'čtvrtek', 'pátek', 'sobota', 'neděle'];
const EVERY_WEEKDAY = ['každé pondělí', 'každé úterý', 'každou středu', 'každý čtvrtek', 'každý pátek', 'každou sobotu', 'každou neděli'];

const fmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: SCHEDULE_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
  weekday: 'short',
});
const WD: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

/** Pražské hodnoty pro okamžik. */
function local(d: Date) {
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  return { y: +p.year, m: +p.month, d: +p.day, hh: +p.hour, mm: +p.minute, ss: +p.second, wd: WD[p.weekday as string] };
}

/** Okamžik pro pražský místní čas (s opravou posunu kolem přechodu času). */
export function pragueToUtc(y: number, m: number, d: number, hh: number, mm: number): Date {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const offsetAt = (t: number) => {
    const l = local(new Date(t));
    return Date.UTC(l.y, l.m - 1, l.d, l.hh, l.mm, l.ss) - t;
  };
  let t = guess - offsetAt(guess);
  t = guess - offsetAt(t);
  return new Date(t);
}

export function validTime(t: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
}

/** Nejbližší spuštění po okamžiku `from`; jednorázová úloha po svém termínu už žádné nemá (null). */
export function nextRun(w: ScheduleWhen, from = new Date()): Date | null {
  if (w.freq === 'once') {
    const at = w.runAt ? new Date(w.runAt) : null;
    return at && at.getTime() > from.getTime() - 60_000 ? at : null;
  }
  const [hh, mm] = w.time.split(':').map(Number);
  const base = local(from);
  for (let i = 0; i <= 8; i++) {
    // Pražský kalendářní den base + i (přes poledne, ať přechod času den neposune).
    const day = local(new Date(Date.UTC(base.y, base.m - 1, base.d + i, 12)));
    const ok = w.freq === 'daily' || (w.freq === 'weekdays' && day.wd <= 5) || (w.freq === 'weekly' && day.wd === w.weekday);
    if (!ok) continue;
    const at = pragueToUtc(day.y, day.m, day.d, hh, mm);
    if (at.getTime() > from.getTime()) return at;
  }
  throw new Error('Nepodařilo se spočítat další spuštění.');
}

export function describeSchedule(w: ScheduleWhen): string {
  if (w.freq === 'once') {
    if (!w.runAt) return 'jednorázově';
    const l = local(new Date(w.runAt));
    return `jednorázově ${l.d}. ${l.m}. v ${l.hh}:${String(l.mm).padStart(2, '0')}`;
  }
  const t = w.time.replace(/^0(\d)/, '$1');
  if (w.freq === 'daily') return `každý den v ${t}`;
  if (w.freq === 'weekdays') return `v pracovní dny v ${t}`;
  return `${EVERY_WEEKDAY[(w.weekday ?? 1) - 1]} v ${t}`;
}

/** Jednorázový termín: za `minutes` minut, nebo pražský čas `YYYY-MM-DDTHH:MM`. Vrací null, když je neplatný. */
export function onceAt(p: { minutes?: number; local?: string }, now = new Date()): Date | null {
  if (p.minutes && p.minutes > 0) return new Date(now.getTime() + p.minutes * 60_000);
  const m = p.local?.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return null;
  return pragueToUtc(+m[1], +m[2], +m[3], +m[4], +m[5]);
}

/** Pražský čas `HH:MM` okamžiku (pro sloupec time u jednorázové úlohy). */
export function pragueTime(d: Date): string {
  const l = local(d);
  return `${String(l.hh).padStart(2, '0')}:${String(l.mm).padStart(2, '0')}`;
}
